/**
 * Which audio codecs can *this* browser actually decode?
 *
 * Asked rather than assumed, because the answer genuinely differs within one household:
 * Edge on Windows decodes E-AC-3 through the platform's Dolby decoder while Chrome on the
 * same machine does not, and Tizen's AVPlay decodes far more than either. A hardcoded
 * table would send some viewers through an unnecessary transcode and leave others in
 * silence.
 *
 * The server uses the answer to decide whether to remux the audio. Sending an empty list
 * — an old client, or a probe that throws — makes it fall back to its own conservative
 * default, which is the safe direction: at worst an unnecessary conversion, never silence.
 */
import { AUDIO_CODEC_PROBES } from '@filmodasi/shared';

let cached: string[] | null = null;

export function decodableAudioCodecs(): string[] {
  if (cached) return cached;

  const probe = document.createElement('video');
  const out: string[] = [];

  for (const [codec, mime] of Object.entries(AUDIO_CODEC_PROBES)) {
    try {
      // canPlayType returns '', 'maybe' or 'probably'. 'maybe' is the honest answer for
      // most codecs and treating it as "no" would transcode almost everything.
      if (probe.canPlayType(mime) !== '') out.push(codec);
    } catch {
      /* a browser that throws here simply does not claim the codec */
    }
  }

  cached = out;
  return out;
}
